import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Eye, Users, FileText, Shield, GraduationCap, Calendar, Ticket as TicketIcon, Activity } from "lucide-react";
import { PageHeader } from "@/pages/admin/AdminShell.jsx";
import { useAuth } from "@/lib/auth.jsx";

const TABS = [
  { k: "overview", label: "Overview", icon: Users },
  { k: "details", label: "Details", icon: FileText },
  { k: "access", label: "Role & access", icon: Shield },
  { k: "training", label: "Training", icon: GraduationCap },
  { k: "leaves", label: "Leaves", icon: Calendar },
  { k: "timeline", label: "Timeline", icon: Activity },
  { k: "tickets", label: "Tickets", icon: TicketIcon },
  { k: "documents", label: "Documents", icon: FileText },
  { k: "activity", label: "Activity", icon: Activity },
];

export default function EmployeeProfile() {
  const { eid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [tab, setTab] = useState("overview");
  const [training, setTraining] = useState({ assignments: [], modules: [] });
  const [leaves, setLeaves] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [note, setNote] = useState({ kind: "praise", text: "", amount: "" });
  const [reveal, setReveal] = useState({});
  const [activity, setActivity] = useState([]);
  const [documents, setDocuments] = useState([]);

  const load = async () => {
    const [e, a, mods, lr, tl, tk] = await Promise.all([
      api.get(`/employees/${eid}`),
      api.get("/training/assignments", { params: { employee_id: eid } }),
      api.get("/training/modules"),
      api.get("/leaves", { params: { employee_id: eid } }),
      api.get(`/timeline/${eid}`),
      api.get("/tickets"),
    ]);
    setEmp(e.data);
    setTraining({ assignments: a.data, modules: mods.data });
    setLeaves(lr.data);
    setTimeline(tl.data);
    setTickets(tk.data.filter((t) => t.employee_id === eid));
    setDocuments((e.data.documents || []).map((d, i) => ({ ...d, i })));
    // activity log: pull audit entries for this employee + comms log
    try {
      const act = await api.get("/activity", { params: { employee_id: eid } });
      setActivity(act.data || []);
    } catch { setActivity([]); }
  };
  useEffect(() => { load(); }, [eid]);

  const addNote = async () => {
    if (!note.text.trim()) return;
    await api.post("/timeline", { employee_id: eid, kind: note.kind, text: note.text, amount: note.amount ? parseFloat(note.amount) : null });
    setNote({ kind: "praise", text: "", amount: "" });
    load(); toast.success("Logged");
  };

  const revealField = async (field) => {
    if (user?.role !== "super_admin") { toast.error("Super admin only"); return; }
    setReveal((r) => ({ ...r, [field]: true }));
    await api.post("/employees/" + eid + "/reveal", { field }).catch(() => {});
  };

  if (!emp) return <div className="text-sm text-[#71717A]">Loading…</div>;

  const trainingPct = (() => {
    const req = training.assignments.filter((a) => (training.modules.find((m) => m.module_id === a.module_id) || {}).required);
    const passed = req.filter((a) => a.status === "passed").length;
    return req.length ? Math.round(passed / req.length * 100) : 0;
  })();

  return (
    <div>
      <button onClick={() => navigate("/admin/team")} className="tt-btn-ghost text-sm flex items-center gap-2 mb-4"><ArrowLeft size={14} /> Back to Team</button>
      <PageHeader
        eyebrow={`PEOPLE · ${emp.employee_number || "EMPLOYEE"}`}
        title={emp.display_name || emp.legal_name}
        subtitle={`${emp.role_title}${emp.team ? " · " + emp.team : ""} · tenure ${emp.tenure_days}d · last active ${emp.last_active ? new Date(emp.last_active).toLocaleString() : "—"}`}
        stats={[
          { label: "Status", value: emp.ready_at ? "Ready" : emp.status, accent: emp.ready_at ? "pass" : "neutral" },
          { label: "Training", value: `${trainingPct}%` },
          { label: "Leaves pending", value: leaves.filter((l) => l.status === "pending" || l.status === "pending_review").length, accent: "warn" },
          { label: "Open tickets", value: tickets.filter((t) => !["resolved","closed"].includes(t.status)).length },
        ]}
      />

      <div className="flex gap-1 border-b border-[#1E1E1E] mb-5 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`profile-tab-${t.k}`}
            className={`px-3 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap ${tab === t.k ? "border-[#F4F4F5] text-[#F4F4F5]" : "border-transparent text-[#71717A] hover:text-[#F4F4F5]"}`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="tt-card p-4"><div className="tt-label mb-2">At a glance</div>
            <Row k="Employee ID" v={emp.employee_number || "—"} /><Row k="Email" v={emp.email} /><Row k="Phone" v={emp.phone || "—"} />
            <Row k="Joining date" v={emp.joining_date || "—"} /><Row k="Probation ends" v={emp.probation_end || "—"} />
          </div>
          <div className="tt-card p-4"><div className="tt-label mb-2">Status</div>
            <Row k="Employment" v={emp.employment_type} /><Row k="Status" v={emp.ready_at ? "Ready" : emp.status} />
            <Row k="Ready at" v={emp.ready_at ? emp.ready_at.slice(0,10) : "—"} /><Row k="Time to ready" v={emp.time_to_ready_days ? `${emp.time_to_ready_days}d` : "—"} />
          </div>
        </div>
      )}

      {tab === "details" && (
        <div className="tt-card p-4 space-y-2 max-w-xl">
          <Row k="Legal name" v={emp.legal_name} /><Row k="Display name" v={emp.display_name || "—"} /><Row k="DOB" v={emp.dob || "—"} />
          <Row k="Address" v={emp.address || "—"} /><Row k="Emergency" v={emp.emergency_contact || "—"} />
          <div className="pt-2 border-t border-[#1E1E1E]"><div className="tt-label mb-2">Sensitive · masked</div>
            <RevealRow k="PAN" v={emp.pan} show={reveal.pan} onReveal={() => revealField("pan")} />
            <RevealRow k="Aadhaar" v={emp.aadhaar} show={reveal.aadhaar} onReveal={() => revealField("aadhaar")} />
            <RevealRow k="Bank" v={emp.bank_account ? `${emp.bank_holder || ""} · ${emp.bank_account} · ${emp.bank_ifsc}` : "—"} show={reveal.bank} onReveal={() => revealField("bank")} />
          </div>
        </div>
      )}

      {tab === "access" && (
        <div className="tt-card p-4 space-y-2 max-w-xl">
          <Row k="Job role" v={emp.role_title} /><Row k="Team" v={emp.team || "—"} />
          <Row k="Reports to" v={emp.reporting_to || "—"} /><Row k="System role" v={emp.email ? "employee" : "—"} />
          <Row k="Buddy" v={emp.buddy_employee_id || "—"} /><Row k="Role track" v={emp.role_track_id || "—"} />
        </div>
      )}

      {tab === "training" && (
        <div className="space-y-2">
          {training.assignments.map((a) => {
            const m = training.modules.find((x) => x.module_id === a.module_id); if (!m) return null;
            return (
              <div key={a.assignment_id} className="tt-card p-4">
                <div className="flex justify-between"><div className="font-medium">{m.title}</div>
                  <span className={a.status === "passed" ? "tt-badge-pass" : a.status === "failed" ? "tt-badge-danger" : "tt-badge-info"}>{a.status.replace("_", " ")}</span>
                </div>
                <div className="text-xs font-mono text-[#71717A] mt-1">attempts {a.attempts}/{m.max_attempts}{a.best_score_pct ? ` · best ${a.best_score_pct}%` : ""}{a.needs_reack_version ? " · needs re-ack" : ""}</div>
              </div>
            );
          })}
          {training.assignments.length === 0 && <Empty hint="Assign a track from Training → Tracks." />}
        </div>
      )}

      {tab === "leaves" && (
        <div className="tt-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0F0F0F]"><tr className="text-[#71717A] text-[10px] font-mono tracking-wider uppercase"><th className="text-left px-4 py-2">Dates</th><th className="text-right px-4 py-2">Days</th><th className="text-left px-4 py-2">Status</th></tr></thead>
            <tbody>{leaves.map((l) => <tr key={l.request_id} className="border-t border-[#1E1E1E]"><td className="px-4 py-3 font-mono text-xs">{l.start_date} → {l.end_date}</td><td className="px-4 py-3 text-right font-mono">{l.days}</td><td className="px-4 py-3"><span className={l.status === "approved" ? "tt-badge-pass" : l.status === "rejected" ? "tt-badge-danger" : "tt-badge-warn"}>{l.status}</span></td></tr>)}
            {leaves.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-[#71717A]">No leaves.</td></tr>}</tbody>
          </table>
        </div>
      )}

      {tab === "timeline" && (
        <div className="tt-card p-4">
          <div className="space-y-2 mb-3">
            {timeline.map((t) => (
              <div key={t.entry_id} className="border-b border-[#1E1E1E] pb-2 last:border-0 text-sm">
                <div className="text-xs font-mono text-[#71717A] flex items-center gap-2">
                  <span className={t.kind === "praise" ? "text-[#22C55E]" : t.kind === "issue" ? "text-[#F5A524]" : t.kind === "admin_note" ? "text-[#71717A]" : "text-[#5B9BFF]"}>{t.kind}</span>
                  <span>·</span><span>{new Date(t.created_at).toLocaleDateString()}</span>
                  {!t.visible_to_employee && <span className="text-[#F5A524]">(internal)</span>}
                </div>
                <div>{t.text}</div>
                {t.amount != null && <div className="font-mono text-xs">₹{t.amount}</div>}
              </div>
            ))}
            {timeline.length === 0 && <Empty hint="Log praise, issues, bonuses or appraisals." />}
          </div>
          <div className="flex gap-2 mb-2">
            <select value={note.kind} onChange={(e) => setNote({ ...note, kind: e.target.value })} className="tt-input !w-32 text-xs">
              {["praise","issue","bonus","appraisal","admin_note"].map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            {note.kind === "bonus" && <input type="number" className="tt-input !w-24 text-xs font-mono" placeholder="₹" value={note.amount} onChange={(e) => setNote({ ...note, amount: e.target.value })} />}
          </div>
          <textarea rows={2} className="tt-input text-sm" value={note.text} onChange={(e) => setNote({ ...note, text: e.target.value })} placeholder="Entry text…" />
          <button onClick={addNote} className="tt-btn-primary text-xs mt-2">Add entry</button>
        </div>
      )}

      {tab === "tickets" && (
        <div className="space-y-2">
          {tickets.map((t) => <div key={t.ticket_id} className="tt-card p-3"><div className="flex justify-between"><div><div className="font-medium">{t.title}</div><div className="text-xs text-[#71717A]">{t.kind}</div></div><span className={t.status === "resolved" || t.status === "closed" ? "tt-badge-pass" : "tt-badge-warn"}>{t.status}</span></div></div>)}
          {tickets.length === 0 && <Empty hint="No tickets filed by this employee." />}
        </div>
      )}

      {tab === "documents" && (
        <div className="space-y-2">
          {documents.map((d, i) => <div key={i} className="tt-card p-3 flex justify-between items-center"><div><div className="font-medium">{d.label || d.file_name || d.kind}</div><div className="text-xs text-[#71717A]">{d.kind}</div></div>{d.file_id && <a className="text-[#5B9BFF] text-xs underline" href={`/api/files/${d.file_id}`}>download</a>}</div>)}
          {documents.length === 0 && <Empty hint="Pre-joining form uploads will appear here when the candidate submits them." />}
        </div>
      )}

      {tab === "activity" && (
        <div className="tt-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0F0F0F]"><tr className="text-[#71717A] text-[10px] font-mono tracking-wider uppercase"><th className="text-left px-4 py-2">When</th><th className="text-left px-4 py-2">Action</th><th className="text-left px-4 py-2">Device/IP</th></tr></thead>
            <tbody>
              {(activity || []).map((a, i) => <tr key={i} className="border-t border-[#1E1E1E]"><td className="px-4 py-2 font-mono text-xs">{new Date(a.ts).toLocaleString()}</td><td className="px-4 py-2">{a.action}</td><td className="px-4 py-2 font-mono text-xs text-[#71717A]">{a.device || "—"} · {a.ip || "—"}</td></tr>)}
              {(!activity || activity.length === 0) && <tr><td colSpan={3} className="px-4 py-6 text-center text-[#71717A]">Last login: {emp.last_login ? new Date(emp.last_login).toLocaleString() : "—"}. Full activity audit coming via last_active ticks.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) { return <div className="flex items-center justify-between py-1 border-b border-[#1E1E1E] last:border-0 text-sm"><span className="text-[#71717A]">{k}</span><span>{v}</span></div>; }
function RevealRow({ k, v, show, onReveal }) {
  return <div className="flex items-center justify-between py-1 border-b border-[#1E1E1E] last:border-0 text-sm"><span className="text-[#71717A]">{k}</span>
    {show || !v ? <span className="font-mono">{v || "—"}</span> : <button onClick={onReveal} className="tt-btn-ghost text-xs flex items-center gap-1"><Eye size={12} /> reveal</button>}
  </div>;
}
function Empty({ hint }) { return <div className="tt-card p-6 text-center text-sm text-[#71717A]">{hint}</div>; }
